// ─────────────────────────────────────────────────────────────────
// CitySearchInput.jsx
// Drop-in replacement for LocationInput on Origin / Destination fields.
// Uses the same master-route city-search API used for intermediate stops.
// ─────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from "react";
import {
  searchMasterRoutesByCitiesForForm,
} from "../../Services/Operations/ProductAPI";

/**
 * Props
 * ─────
 * token          – JWT auth token
 * value          – controlled city string shown in the input
 * onChange       – (cityString) => void   (raw text change)
 * onCitySelect   – (cityObj) => void      called when user picks a suggestion
 *                  cityObj = { city, state, address, terminal, coordinates, type }
 * placeholder    – string
 * disabled       – bool
 */
const CitySearchInput = ({
  token,
  value,
  onChange,
  onCitySelect,
  placeholder = "Type city name (min 2 characters)...",
  disabled = false,
  searchType = 'both', // Add this prop: 'origin', 'destination', or 'both'
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef(null);

  // Keep the input value in sync if the parent resets it
  useEffect(() => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [value]);

 const searchCities = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setIsLoading(true);
    try {
      // Pass the searchType to the API
      const response = await searchMasterRoutesByCitiesForForm(token, query, searchType);
      if (response?.success && response?.data?.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error("CitySearchInput error:", err);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);

    // Clear dropdown immediately on every keystroke;
    // re-open only when the debounced fetch returns results.
    setShowDropdown(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => searchCities(val), 400);
  };

  const handleSelect = (city) => {
    // Show the city name in the input
    onChange(city.city || "");
    setSuggestions([]);
    setShowDropdown(false);

    // Bubble the full object up so the parent can auto-fill the other fields
    if (onCitySelect) {
      onCitySelect({
        city: city.city || "",
        state: city.state || "",
        address: city.address || `${city.city || ""}, ${city.state || "India"}`,
        terminal: city.terminal || `${city.city || ""} Bus Stand`,
        coordinates: city.coordinates || { lat: 0, lng: 0 },
        type: city.type || "",
      });
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* ── Input ── */}
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={value || ""}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={placeholder}
          className="form-input"
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <div
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <div className="spinner-small" />
          </div>
        )}
      </div>

      {/* ── Dropdown ── */}
      {showDropdown && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "6px",
            maxHeight: "220px",
            overflowY: "auto",
            zIndex: 10000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "6px 12px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #eee",
              fontSize: "11px",
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>📍</span>
            <span>{suggestions.length} location(s) found</span>
          </div>

          {/* Items */}
          {suggestions.map((city, idx) => (
            <div
              key={idx}
              onMouseDown={() => handleSelect(city)}   // mouseDown fires before blur
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderBottom: "0.5px solid #f0f0f0",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f0f7ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              {/* City name + type badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 3,
                }}
              >
                <span style={{ fontWeight: "500", fontSize: "14px" }}>
                  {city.city}
                </span>
                {city.type && (
                  <span
                    style={{
                      fontSize: "10px",
                      padding: "1px 7px",
                      borderRadius: "999px",
                      backgroundColor:
                        city.type === "origin" ? "#e6f1fb" : "#eaf3de",
                      color: city.type === "origin" ? "#185fa5" : "#3b6d11",
                      fontWeight: "500",
                      textTransform: "capitalize",
                    }}
                  >
                    {city.type}
                  </span>
                )}
              </div>

              {/* State */}
              {city.state && (
                <div style={{ fontSize: "12px", color: "#6c757d" }}>
                  {city.state}
                  {city.terminal ? ` · ${city.terminal}` : ""}
                </div>
              )}

              {/* Address (only if distinct from city name) */}
              {city.address &&
                city.address !== city.city &&
                !city.address.startsWith(city.city + ",") && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#999",
                      marginTop: "2px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {city.address}
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitySearchInput;